import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWA, useLoad, useDebounced, useSelection, PageHead, Card, Tabs, Search, Btn, Badge, StatusBadge, Check, BulkBar, Pager, Skeleton, Empty, ErrorBox, num, ago, exportCsv } from "../ui";
import { absUrl, siteLink } from "../api";

const STATUS_TABS = [["", "सभी"], ["PUBLISHED", "Published"], ["DRAFT", "Draft"], ["SCHEDULED", "Scheduled"], ["PRIVATE", "Private"], ["UNLISTED", "Unlisted"]];
const MON = { NONE: null, REQUESTED: ["Requested", "amber"], APPROVED: ["Monetized", "green"], REJECTED: ["Rejected", "red"] };
const TAKE = 30;

export default function Blogs() {
  const { run, confirm, prompt } = useWA();
  const nav = useNavigate();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const [status, setStatus] = useState(qs.get("status") || "");
  const [mon, setMon] = useState(qs.get("monetization") || "");
  const [cat, setCat] = useState(qs.get("category") || "");
  const [sort, setSort] = useState("updated");
  const [pillar, setPillar] = useState(false);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const dq = useDebounced(q);

  useEffect(() => setSkip(0), [status, mon, cat, sort, pillar, dq]);
  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    if (p.get("status") !== null) setStatus(p.get("status"));
    if (p.get("monetization") !== null) setMon(p.get("monetization"));
  }, [loc.search]);

  const cats = useLoad("categories");
  const { data, loading, error, reload } = useLoad("blogs", { q: dq, status, monetization: mon, categoryId: cat, sort, pillar, take: TAKE, skip }, [dq, status, mon, cat, sort, pillar, skip]);
  const items = (data && data.items) || [];
  const S = useSelection(items);

  const act = async (action, params, msg, ask) => {
    if (ask && !(await confirm(ask))) return;
    try {
      await run(action, params, msg);
      reload();
    } catch (e) {
      /* toast shown */
    }
  };

  const monetize = async (value) => {
    let note = "";
    if (value === "REJECTED") {
      note = await prompt({ title: "Reject का कारण", text: "यह कारण author को दिखेगा।", defaultValue: "Traffic या quality अभी कम है" });
      if (note === null) return;
    }
    act("monetize", { ids: S.sel, value, note, notify: true }, value === "APPROVED" ? "Monetization approve हो गया, author को message गया" : "Monetization update हो गया");
  };

  const moveCategory = async () => {
    const list = (cats.data && cats.data.items) || [];
    const names = list.map((c, i) => `${i + 1}. ${c.name}`).join("\n");
    const v = await prompt({ title: "Category बदलें", text: `नंबर लिखें (0 = कोई category नहीं)\n${names}`, defaultValue: "1" });
    if (v === null) return;
    const n = Number(v);
    const c = n === 0 ? null : list[n - 1];
    if (n !== 0 && !c) return;
    act("blogCategory", { ids: S.sel, categoryId: c ? c.id : null }, "Category बदल गई");
  };

  return (
    <div>
      <PageHead icon="fa-solid fa-pen-nib" title="Blogs" subtitle={data ? `कुल ${num(data.total)} blogs` : "वेबसाइट के सारे blogs"}
        actions={
          <>
            <Btn icon="fa-solid fa-download" disabled={!items.length} onClick={() => exportCsv(items.map((b) => ({ title: b.title, slug: b.slug, status: b.status, author: b.author, category: b.category, views: b.views, likes: b.likes, comments: b.comments, monetization: b.monetization, updated: b.at })), "ycnp-blogs")}>CSV</Btn>
            <Btn variant="primary" icon="fa-solid fa-plus" onClick={() => nav("/website/blogs/new")}>नया Blog</Btn>
          </>
        } />

      <div className="flex flex-col gap-3 mb-4">
        <Tabs tabs={STATUS_TABS} value={status} onChange={setStatus} />
        <div className="flex flex-wrap gap-2">
          <Search value={q} onChange={setQ} placeholder="Title या slug से खोजें…" className="flex-1 min-w-[220px]" />
          <select className="wa-input !w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="Monetization">
            <option value="">सभी monetization</option>
            <option value="REQUESTED">Requested</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="NONE">None</option>
          </select>
          <select className="wa-input !w-auto" value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
            <option value="">सभी categories</option>
            <option value="none">बिना category</option>
            {((cats.data && cats.data.items) || []).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.blogs})</option>)}
          </select>
          <select className="wa-input !w-auto" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
            <option value="updated">नया बदला पहले</option>
            <option value="published">नया publish पहले</option>
            <option value="views">सबसे ज़्यादा views</option>
            <option value="title">Title A–Z</option>
            <option value="old">पुराना पहले</option>
          </select>
          <Btn variant={pillar ? "soft" : "ghost"} icon="fa-solid fa-star" onClick={() => setPillar((v) => !v)}>Pillar</Btn>
        </div>
      </div>

      <BulkBar count={S.sel.length} onClear={S.clear}>
        <Btn size="sm" variant="success" icon="fa-solid fa-globe" onClick={() => act("blogStatus", { ids: S.sel, status: "PUBLISHED" }, "Publish हो गए")}>Publish</Btn>
        <Btn size="sm" variant="ghost" className="!text-white !border-white/20" onClick={() => act("blogStatus", { ids: S.sel, status: "DRAFT" }, "Draft में गए")}>Draft</Btn>
        <Btn size="sm" variant="ghost" className="!text-white !border-white/20" onClick={() => act("blogStatus", { ids: S.sel, status: "PRIVATE" }, "Private हो गए")}>Private</Btn>
        <Btn size="sm" variant="ghost" className="!text-white !border-white/20" onClick={() => act("blogStatus", { ids: S.sel, status: "UNLISTED" }, "Unlisted हो गए")}>Unlisted</Btn>
        <Btn size="sm" variant="ghost" className="!text-white !border-white/20" icon="fa-solid fa-star" onClick={() => act("blogPillar", { ids: S.sel, value: true }, "Pillar बन गए")}>Pillar</Btn>
        <Btn size="sm" variant="ghost" className="!text-white !border-white/20" icon="fa-solid fa-tag" onClick={moveCategory}>Category</Btn>
        <Btn size="sm" variant="success" icon="fa-solid fa-sack-dollar" onClick={() => monetize("APPROVED")}>Monetize ✓</Btn>
        <Btn size="sm" variant="warn" onClick={() => monetize("REJECTED")}>Monetize ✗</Btn>
        <Btn size="sm" variant="danger" icon="fa-regular fa-trash-can" onClick={() => act("blogDelete", { ids: S.sel }, "Delete हो गए", { title: `${S.sel.length} blogs हमेशा के लिए delete करें?`, text: "इनके likes, comments और views भी हट जाएँगे। यह वापस नहीं होगा।", danger: true })}>Delete</Btn>
      </BulkBar>

      <ErrorBox msg={error} retry={reload} />
      <Card pad={false}>
        {loading && !data ? <div className="p-4"><Skeleton rows={8} h={52} /></div> : !items.length ? (
          <Empty icon="fa-solid fa-pen-nib" title="कोई blog नहीं मिला" text="Filter बदलकर देखें या नया blog लिखें।" action={<Btn variant="primary" icon="fa-solid fa-plus" onClick={() => nav("/website/blogs/new")}>नया Blog</Btn>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="wa-tbl">
              <thead>
                <tr>
                  <th style={{ width: 36 }}><Check checked={S.all} onChange={S.toggleAll} label="Select all" /></th>
                  <th>Blog</th>
                  <th>Status</th>
                  <th className="text-right">Views</th>
                  <th className="text-right hidden md:table-cell">♥ / 💬</th>
                  <th className="hidden lg:table-cell">Monetization</th>
                  <th className="hidden md:table-cell">Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className={S.has(b.id) ? "wa-sel" : ""}>
                    <td><Check checked={S.has(b.id)} onChange={() => S.toggle(b.id)} /></td>
                    <td className="min-w-[260px]">
                      <div className="flex items-center gap-3">
                        {b.cover ? <img src={absUrl(b.cover)} alt="" className="w-14 h-10 rounded-lg object-cover shrink-0 border wa-line" /> : <span className="w-14 h-10 rounded-lg shrink-0 grid place-items-center wa-muted" style={{ background: "color-mix(in srgb, var(--wa-ink) 6%, transparent)" }}><i className="fa-regular fa-image" /></span>}
                        <div className="min-w-0">
                          <Link to={`/website/blogs/${b.id}`} className="font-semibold hover:underline line-clamp-1">{b.pillar && <i className="fa-solid fa-star text-amber-500 text-[11px] mr-1" title="Pillar post" />}{b.title}</Link>
                          <p className="text-[11.5px] wa-muted truncate">{b.author}{b.category ? ` · ${b.category}` : ""}</p>
                        </div>
                      </div>
                    </td>
                    <td><StatusBadge s={b.status} />{b.status === "SCHEDULED" && b.publishedAt && <p className="text-[10.5px] wa-muted mt-0.5">{new Date(b.publishedAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</p>}</td>
                    <td className="text-right tabular-nums font-semibold">{num(b.views)}</td>
                    <td className="text-right tabular-nums hidden md:table-cell wa-muted">{num(b.likes)} / {num(b.comments)}</td>
                    <td className="hidden lg:table-cell">{MON[b.monetization] ? <Badge tone={MON[b.monetization][1]}>{MON[b.monetization][0]}</Badge> : <span className="wa-muted">—</span>}{b.monetNote && <p className="text-[10.5px] wa-muted mt-0.5 max-w-[160px] truncate" title={b.monetNote}>{b.monetNote}</p>}</td>
                    <td className="hidden md:table-cell text-[12px] wa-muted whitespace-nowrap">{ago(b.at)}</td>
                    <td className="text-right whitespace-nowrap">
                      <Btn size="sm" variant="ghost" icon="fa-regular fa-pen-to-square" onClick={() => nav(`/website/blogs/${b.id}`)} aria-label="Edit" title="Edit" />
                      <a href={siteLink(`/blogs/${encodeURIComponent(b.slug)}`)} target="_blank" rel="noreferrer" className="inline-block ml-1"><Btn size="sm" variant="ghost" icon="fa-solid fa-arrow-up-right-from-square" aria-label="Open on site" title="Site पर देखें" /></a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={data && data.total} take={TAKE} skip={skip} onChange={setSkip} />
      </Card>
    </div>
  );
}
